import React from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Loading / error state for one dashboard section.
 *
 * Wraps a card and lays a spinner over it while its data is being fetched — on
 * first load AND on every class/term/year change, which RTK Query's `isLoading`
 * does not cover (it is only true before a hook has ever had data). When the
 * request failed, it says so with a Retry button instead of letting the card show
 * "No data" / "All clear!", which looked identical to a genuinely empty result.
 *
 * The card stays rendered underneath (dimmed), so the layout doesn't jump.
 */

/** The parts of an RTK Query hook result this needs. */
export interface QueryLike {
  isFetching: boolean;
  isError: boolean;
  refetch: () => unknown;
}

export interface SectionState {
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

/** Combines the queries a section depends on: loading if any is, failed if any did. */
export const sectionState = (...queries: QueryLike[]): SectionState => {
  const loading = queries.some((q) => q.isFetching);
  return {
    loading,
    failed: !loading && queries.some((q) => q.isError),
    retry: () => queries.forEach((q) => { if (q.isError) q.refetch(); }),
  };
};

interface Props {
  state: SectionState;
  /** Shown in the spinner and error text, e.g. "top performers". */
  label?: string;
  /** Keeps a hidden-until-data card visible as a placeholder while loading. */
  minHeight?: number;
  className?: string;
  children?: React.ReactNode;
}

export const QuerySection: React.FC<Props> = ({ state, label, minHeight, className = '', children }) => {
  const { loading, failed, retry } = state;
  const overlay = loading || failed;
  // A section whose card only renders once data exists has nothing to dim — give
  // the placeholder a card frame so the spinner / error doesn't float on the page.
  const placeholder = overlay && React.Children.toArray(children).length === 0;

  return (
    <div
      className={`relative ${placeholder ? 'rounded-2xl border border-gray-200 bg-white shadow-sm' : ''} ${className}`}
      style={overlay && minHeight ? { minHeight } : undefined}
      aria-busy={loading}
    >
      {/* h-full lets a card fill a stretched grid cell (MetricsRow layout="grid");
          inside an auto-height section it resolves to auto and changes nothing. */}
      <div className={`h-full ${overlay ? 'opacity-40 pointer-events-none select-none transition-opacity' : 'transition-opacity'}`}>
        {children}
      </div>

      {loading && (
        <div role="status" className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl">
          <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
          <span className="text-xs font-medium text-gray-600">Loading{label ? ` ${label}` : ''}…</span>
        </div>
      )}

      {failed && (
        <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl px-4 text-center">
          <AlertTriangle className="h-6 w-6 text-amber-500" />
          <span className="text-sm font-medium text-gray-700">Couldn&apos;t load{label ? ` ${label}` : ' this section'}.</span>
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}
    </div>
  );
};

export default QuerySection;
